// MeetingDetailsPage.js - Clean, minimal meeting details page
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
  Stack,
  Tabs,
  Tab,
  Chip,
  Button,
  Container,
  Divider,
  Avatar,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
  Person as PersonIcon,
  Event as EventIcon,
} from "@mui/icons-material";
import {
  fetchMeetingRecording,
  fetchMeetingTranscript,
  fetchMeetingSummary,
} from "./utils/meetingDataUtils";
import { useQuery } from "@apollo/client";
import { GET_BOOKING_BY_ID } from "../../../graphql/queries";
import { axiosInstance } from "../../../utils/axios";
import RealTimeTranscript from "../../../components/RealTimeTranscript";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function MeetingDetailsPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [transcriptData, setTranscriptData] = useState(null);
  const [eventTypeData, setEventTypeData] = useState(null);
  const [copied, setCopied] = useState(false);

  // Fetch booking details
  const {
    data: bookingData,
    loading: bookingLoading,
    error: bookingError,
  } = useQuery(GET_BOOKING_BY_ID, {
    variables: { id: bookingId },
    skip: !bookingId,
  });

  const booking = bookingData?.getBooking;

  // Fetch event type data
  const fetchEventTypeData = async (eventTypeId) => {
    try {
      const response = await axiosInstance.post("/graphql", {
        query: `
          query GetEventType($id: ID!) {
            getEventType(id: $id) {
              id
              title
              url
              description
              duration
              assignment
              availabilityId
              classification {
                eventFormat
                templateType
                customTemplate
              }
              advanced {
                requiresConfirmation {
                  enabled
                }
              }
            }
          }
        `,
        variables: { id: eventTypeId },
      });

      if (response.data?.data?.getEventType) {
        setEventTypeData(response.data.data.getEventType);
      } else if (response.data?.getEventType) {
        setEventTypeData(response.data.getEventType);
      }
    } catch (error) {
      console.error("Error fetching event type:", error);
    }
  };

  useEffect(() => {
    if (booking?.externalMeetingId) {
      fetchMeetingData();
    }
    if (booking?.eventTypeId) {
      fetchEventTypeData(booking.eventTypeId);
    }
  }, [booking]);

  const fetchMeetingData = async () => {
    if (!booking?.externalMeetingId) {
      setError("No external meeting ID found for this booking");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch summary and transcript in parallel
      const [transcript, summary] = await Promise.allSettled([
        fetchMeetingTranscript(booking.externalMeetingId),
        fetchMeetingSummary(booking.externalMeetingId),
      ]);

      // Handle transcript data
      if (transcript.status === "fulfilled") {
        setTranscriptData(transcript.value);
      }

      // Handle summary data
      if (summary.status === "fulfilled") {
        setSummaryData(summary.value);
      } else if (summary.reason) {
        console.error("Error fetching summary:", summary.reason);
      }

      // Check if all failed
      if (transcript.status === "rejected" && summary.status === "rejected") {
        setError(
          "Unable to fetch meeting data. The data might not be ready yet."
        );
      }
    } catch (err) {
      console.error("Error fetching meeting data:", err);
      setError("An error occurred while fetching meeting data");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleCopySummary = () => {
    if (summaryData?.summary) {
      navigator.clipboard.writeText(summaryData.summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const renderSummaryContent = () => {
    if (!summaryData) {
      return (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            No summary available yet. Please check back later.
          </Typography>
        </Box>
      );
    }

    // Handle the structured response from the API
    if (summaryData.summary || summaryData.tasks) {
      return (
        <Box>
          {/* Overview Section */}
          {summaryData.summary && (
            <Box mb={6}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  fontSize: "18px",
                  letterSpacing: "-0.01em",
                }}
              >
                Overview
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.8,
                  color: "#4b5563",
                  fontSize: "15px",
                }}
              >
                {summaryData.summary}
              </Typography>
            </Box>
          )}

          {/* Action Items Section - Todo List Style */}
          {summaryData.tasks && summaryData.tasks.length > 0 && (
            <Box mb={6}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  fontSize: "18px",
                  letterSpacing: "-0.01em",
                }}
              >
                Action Items
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: "#fafbfc",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  p: 0,
                }}
              >
                <Stack divider={<Divider />}>
                  {summaryData.tasks.map((task, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2.5,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        "&:hover": {
                          backgroundColor: "rgba(99, 102, 241, 0.02)",
                        },
                        transition: "background-color 0.15s ease",
                      }}
                    >
                      <CheckBoxOutlineBlankIcon
                        sx={{
                          fontSize: 20,
                          color: "#9ca3af",
                          mt: 0.3,
                          cursor: "pointer",
                          "&:hover": {
                            color: "#6366f1",
                          },
                        }}
                      />
                      <Box flex={1}>
                        <Typography
                          variant="body1"
                          sx={{
                            mb: 1,
                            fontWeight: 500,
                            fontSize: "15px",
                            lineHeight: 1.6,
                            color: "#1a1a1a",
                          }}
                        >
                          {task.task}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          {task.assignee &&
                            task.assignee !== "To be determined" && (
                              <Chip
                                icon={<PersonIcon />}
                                label={task.assignee}
                                size="small"
                                sx={{
                                  backgroundColor: "#e5e7eb",
                                  color: "#4b5563",
                                  fontWeight: 500,
                                  fontSize: "13px",
                                  "& .MuiChip-icon": {
                                    fontSize: 16,
                                    color: "#6b7280",
                                  },
                                }}
                              />
                            )}
                          {task.due_date && (
                            <Chip
                              icon={<EventIcon />}
                              label={`Due ${new Date(
                                task.due_date
                              ).toLocaleDateString()}`}
                              size="small"
                              sx={{
                                backgroundColor: "#fef3c7",
                                color: "#92400e",
                                fontWeight: 500,
                                fontSize: "13px",
                                "& .MuiChip-icon": {
                                  fontSize: 16,
                                  color: "#d97706",
                                },
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Box>
          )}

          {/* Insights Section */}
          {summaryData.insights && (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  fontSize: "18px",
                  letterSpacing: "-0.01em",
                }}
              >
                Insights
              </Typography>

              <Stack spacing={3}>
                {summaryData.insights.achievements && (
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: "#ecfdf5",
                      border: "1px solid #86efac",
                      borderRadius: 1.5,
                      p: 2.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        color: "#059669",
                        fontWeight: 600,
                        fontSize: "14px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Achievements
                    </Typography>
                    <Stack spacing={1.5}>
                      {summaryData.insights.achievements.map(
                        (achievement, index) => (
                          <Box
                            key={index}
                            display="flex"
                            alignItems="flex-start"
                            gap={1.5}
                          >
                            <CheckIcon
                              sx={{ fontSize: 16, color: "#059669", mt: 0.3 }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#047857",
                                fontSize: "14px",
                                lineHeight: 1.6,
                              }}
                            >
                              {achievement}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Stack>
                  </Paper>
                )}

                {summaryData.insights.challenges && (
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: "#fef3c7",
                      border: "1px solid #fde68a",
                      borderRadius: 1.5,
                      p: 2.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        color: "#d97706",
                        fontWeight: 600,
                        fontSize: "14px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Challenges
                    </Typography>
                    <Stack spacing={1.5}>
                      {summaryData.insights.challenges.map(
                        (challenge, index) => (
                          <Box
                            key={index}
                            display="flex"
                            alignItems="flex-start"
                            gap={1.5}
                          >
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: "#d97706",
                                mt: 0.8,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#92400e",
                                fontSize: "14px",
                                lineHeight: 1.6,
                              }}
                            >
                              {challenge}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Box>
          )}
        </Box>
      );
    }

    // Fallback for string data
    if (typeof summaryData === "string") {
      return (
        <Typography
          variant="body1"
          sx={{
            whiteSpace: "pre-wrap",
            lineHeight: 1.8,
            color: "text.secondary",
          }}
        >
          {summaryData}
        </Typography>
      );
    }

    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          Summary data is not in the expected format.
        </Typography>
      </Box>
    );
  };

  const renderTranscriptContent = () => {
    // Check if we have an external meeting ID to connect to real-time transcript
    if (!booking?.externalMeetingId) {
      return (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            No meeting recording available for this booking.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 3,
            fontSize: "18px",
            letterSpacing: "-0.01em",
          }}
        >
          Real-Time Transcript
        </Typography>

        <RealTimeTranscript
          meetingId={booking.externalMeetingId}
          websocketUrl={
            process.env.REACT_APP_WEBSOCKET_URL ||
            "wss://livebots.instaconnect.io/ws"
          }
          showAudioPlayer={false}
          autoScroll={true}
          sx={{
            minHeight: "400px",
            maxHeight: "600px",
          }}
        />

        {/* Fallback: Show static transcript if available */}
        {transcriptData && (
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: "14px",
                color: "text.secondary",
              }}
            >
              Static Transcript (Backup)
            </Typography>
            <Box
              sx={{
                fontFamily: "monospace",
                fontSize: "13px",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                color: "text.secondary",
                backgroundColor: "grey.50",
                p: 2,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {typeof transcriptData === "string"
                ? transcriptData
                : JSON.stringify(transcriptData, null, 2)}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  if (bookingLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (bookingError || !booking) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          {bookingError?.message || "Booking not found"}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8f9fa",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      }}
    >
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Enhanced Header */}
        <Box mb={5}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <IconButton
              onClick={handleGoBack}
              sx={{
                backgroundColor: "white",
                border: "1px solid",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: "grey.50",
                  borderColor: "grey.400",
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <Box flex={1}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: "28px",
                  letterSpacing: "-0.02em",
                  color: "#1a1a1a",
                }}
              >
                {eventTypeData?.title || "Meeting"}
              </Typography>
            </Box>

            {/* Copy Summary Button */}
            <Button
              variant="outlined"
              size="medium"
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={handleCopySummary}
              disabled={!summaryData?.summary}
              sx={{
                borderColor: copied ? "success.main" : "divider",
                color: copied ? "success.main" : "text.primary",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  borderColor: copied ? "success.main" : "text.primary",
                  backgroundColor: copied ? "success.light" : "grey.50",
                },
              }}
            >
              {copied ? "Copied!" : "Copy Summary"}
            </Button>
          </Box>

          {/* Enhanced Meeting Info Card */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              backgroundColor: "white",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Stack direction="row" spacing={4} alignItems="center">
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    fontSize: "16px",
                    fontWeight: 600,
                    backgroundColor: "#6366f1",
                    color: "white",
                  }}
                >
                  {booking.name?.charAt(0).toUpperCase() || "U"}
                </Avatar>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "#1a1a1a" }}
                  >
                    {booking.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {booking.email}
                  </Typography>
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem />

              <Box display="flex" alignItems="center" gap={1}>
                <CalendarIcon sx={{ fontSize: 18, color: "#6366f1" }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDate(booking.date)}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <AccessTimeIcon sx={{ fontSize: 18, color: "#6366f1" }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Time
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatTime(booking.timeStart)} •{" "}
                    {eventTypeData?.duration || 30} min
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {/* Enhanced Main Content */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          {/* Enhanced Tabs */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "text.secondary",
                  minHeight: 56,
                  letterSpacing: "-0.01em",
                  "&.Mui-selected": {
                    color: "#6366f1",
                  },
                  "&:hover": {
                    color: "text.primary",
                    backgroundColor: "transparent",
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#6366f1",
                  height: 3,
                },
              }}
            >
              <Tab label="Summary" />
              <Tab label="Transcript" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ p: 4 }}>
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="300px"
              >
                <CircularProgress sx={{ color: "#6366f1" }} />
              </Box>
            ) : error ? (
              <Alert
                severity="warning"
                sx={{
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "warning.light",
                }}
              >
                {error}
              </Alert>
            ) : (
              <>
                <TabPanel value={tabValue} index={0}>
                  {renderSummaryContent()}
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                  {renderTranscriptContent()}
                </TabPanel>
              </>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default MeetingDetailsPage;
